import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { useEffect, useRef } from 'react';

export type ConfigType =
  | 'pipe_material'
  | 'reporting_type'
  | 'pipe_diameter'
  | 'technical_allocation'
  | 'damage_type'
  | 'repair_material'
  | 'zones'
  | 'supply_pressure'
  | 'hole_size';

export interface CustomValue {
  id: string;
  label: string;
  value: string;
  enabled: boolean;
}

export interface ConfigValue {
  id: string;
  label: string;
  value: string;
  enabled: boolean;
}

export interface RequestConfig {
  configType: ConfigType;
  useDefaults: boolean;
  defaultValues: ConfigValue[];
  customValues: CustomValue[];
}

interface RequestConfigResponse {
  success: boolean;
  config: RequestConfig;
}

interface CustomValuePayload {
  label: string;
  value: string;
}

interface UpdateConfigPayload {
  useDefaults: boolean;
  customValues: CustomValue[];
}

// Query key factory for setup configs
const setupConfigsKeys = {
  all: ['setupConfigs'] as const,
  list: () => [...setupConfigsKeys.all, 'list'] as const,
  single: (configType: ConfigType) => [...setupConfigsKeys.all, 'single', configType] as const,
};

/**
 * Hook to fetch a single setup configuration by type
 * Caches data for 5 minutes, automatically refetches when configType changes
 */
export function useSetupConfig(configType: ConfigType) {
  return useQuery<RequestConfigResponse>({
    queryKey: setupConfigsKeys.single(configType),
    queryFn: async () => {
      const response = await api.get<RequestConfigResponse>(
        `/setup/request-configs/${configType}`
      );
      return response;
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 10, // 10 minutes
  });
}

/**
 * Hook to add a custom value to a config
 * Invalidates the specific config cache when done
 */
export function useAddCustomValue(configType: ConfigType) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: CustomValuePayload) => {
      const response = await api.post<RequestConfigResponse>(
        `/setup/request-configs/${configType}/values`,
        payload
      );
      return response;
    },
    onSuccess: () => {
      // Invalidate the config so it refetches
      queryClient.invalidateQueries({
        queryKey: setupConfigsKeys.single(configType),
      });
    },
  });
}

/**
 * Hook to delete a custom value from a config
 * Invalidates the specific config cache when done
 */
export function useDeleteCustomValue(configType: ConfigType) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (valueId: string) => {
      const response = await api.delete<RequestConfigResponse>(
        `/setup/request-configs/${configType}/values/${valueId}`
      );
      return response;
    },
    onSuccess: () => {
      // Invalidate the config so it refetches
      queryClient.invalidateQueries({
        queryKey: setupConfigsKeys.single(configType),
      });
    },
  });
}

/**
 * Hook to update config settings (useDefaults, customValues)
 * Invalidates the specific config cache when done
 */
export function useUpdateConfig(configType: ConfigType) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: UpdateConfigPayload) => {
      const response = await api.put<RequestConfigResponse>(
        `/setup/request-configs/${configType}`,
        payload
      );
      return response;
    },
    onSuccess: () => {
      // Invalidate the config so it refetches
      queryClient.invalidateQueries({
        queryKey: setupConfigsKeys.single(configType),
      });
    },
  });
}

/**
 * Hook to reactivate a deleted custom value
 * Invalidates the specific config cache when done
 */
export function useReactivateCustomValue(configType: ConfigType) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (valueId: string) => {
      const response = await api.put<RequestConfigResponse>(
        `/setup/request-configs/${configType}/values/${valueId}/reactivate`,
        {}
      );
      return response;
    },
    onSuccess: () => {
      // Invalidate the config so it refetches
      queryClient.invalidateQueries({
        queryKey: setupConfigsKeys.single(configType),
      });
    },
  });
}

/**
 * Hook to permanently delete a custom value
 * Invalidates the specific config cache when done
 */
export function usePermanentlyDeleteCustomValue(configType: ConfigType) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (valueId: string) => {
      const response = await api.delete<RequestConfigResponse>(
        `/setup/request-configs/${configType}/values/${valueId}/permanently`
      );
      return response;
    },
    onSuccess: () => {
      // Invalidate the config so it refetches
      queryClient.invalidateQueries({
        queryKey: setupConfigsKeys.single(configType),
      });
    },
  });
}

/**
 * Hook to poll for config version changes (for multi-user cache sync)
 * Detects when other users modify configs and invalidates cache
 * Polls every 5 minutes by default
 */
export function useConfigVersionPolling(
  configType: ConfigType | null,
  options?: { enabled?: boolean; interval?: number }
) {
  const queryClient = useQueryClient();
  const versionRef = useRef<string | null>(null);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (!configType || options?.enabled === false) {
      // Cleanup
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      return;
    }

    const pollInterval = options?.interval ?? 1000 * 60 * 5; // 5 minutes default

    const checkVersion = async () => {
      try {
        const response = await api.get<{
          configType: ConfigType;
          version: string;
          timestamp: number;
        }>(`/setup/request-configs/${configType}/version`);

        if (response && response.version) {
          // First time - just store the version
          if (versionRef.current === null) {
            versionRef.current = response.version;
            return;
          }

          // If version changed, invalidate cache
          if (versionRef.current !== response.version) {
            console.log(
              `[ConfigPolling] Version changed for ${configType}. Invalidating cache.`
            );
            versionRef.current = response.version;
            queryClient.invalidateQueries({
              queryKey: setupConfigsKeys.single(configType),
            });
          }
        }
      } catch (error) {
        console.error(`[ConfigPolling] Error checking version for ${configType}:`, error);
        // Silently fail - polling will retry on next interval
      }
    };

    // Initial check
    checkVersion();

    // Set up polling
    intervalRef.current = setInterval(checkVersion, pollInterval);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [configType, options?.enabled, options?.interval, queryClient]);
}
